import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { usePersonEvents } from '../../hooks/usePeople';
import { Button } from '../ui/Button';
import { SkeletonList } from '../ui/Skeleton';
import { TasksPanel } from './TasksPanel';

/**
 * Shortcut modal for editing a person's tasks without opening their
 * full admin edit page. Wraps TasksPanel — same UI as PersonEdit so
 * coordinators get one mental model for tasks regardless of where
 * they're working from.
 *
 * Loads the event chain on open; TasksPanel projects ActiveTask[]
 * from events client-side and writes new TaskAssigned / TaskUpdated /
 * TaskApproved / TaskRejected events back. We invalidate the events
 * query on `onChange` so the projected list resyncs after each write.
 */
export function PersonTasksModal({
  open,
  personId,
  personName,
  onClose,
}: {
  open: boolean;
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const eventsQ = usePersonEvents(open ? personId : null);
  const qc = useQueryClient();

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="person-tasks-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-fg/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 -z-10"
      />
      <div className="aurora-fade-in flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-center justify-between gap-2 border-b border-divider/10 px-5 py-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-accent">
              Tasks
            </div>
            <h2
              id="person-tasks-title"
              className="truncate text-base font-semibold tracking-tight text-fg"
            >
              {personName}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {eventsQ.isLoading ? (
            <SkeletonList rows={3} />
          ) : (
            <TasksPanel
              personId={personId}
              events={eventsQ.data ?? []}
              onChange={() => {
                // TasksPanel already invalidates ['people'] via the
                // append-event hook; refetch this person's events
                // explicitly so the projected list updates in-place.
                qc.invalidateQueries({
                  queryKey: ['people', personId, 'events'],
                });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
